import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, where, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { handleFirestoreError, OperationType } from '../lib/firestore-errors';
import { Calendar, Trash2, ExternalLink, Copy, CheckCircle2, RefreshCw } from 'lucide-react';
import { format } from 'date-fns';

export function BookingsView({ hostId }: { hostId: string }) {
  const [bookings, setBookings] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [showConfirmClear, setShowConfirmClear] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  useEffect(() => {
    if (!hostId) return;
    const fetchBookings = async () => {
      try {
        const bookingsQuery = query(collection(db, 'bookings'), where('hostId', '==', hostId));
        const bookingDocs = await getDocs(bookingsQuery);
        const fetchedBookings = bookingDocs.docs.map(d => ({ id: d.id, ...d.data() }));

        const guestsQuery = query(collection(db, 'booking_guests'), where('hostId', '==', hostId));
        const guestDocs = await getDocs(guestsQuery);
        const guestData: Record<string, any> = {};
        guestDocs.docs.forEach(d => {
          guestData[d.id] = d.data();
        });

        const combinedBookings = fetchedBookings.map((b: any) => ({
          ...b,
          guestName: guestData[b.id]?.guestName || 'Unknown Guest',
          guestEmail: guestData[b.id]?.guestEmail || 'Unknown Email'
        })).sort((a: any, b: any) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
        
        setBookings(combinedBookings);
      } catch (err) {
        console.error("Error fetching bookings:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchBookings();
  }, [hostId]);

  const handleDelete = async (bookingId: string) => {
    try {
      console.log("Attempting to delete booking:", bookingId);
      await deleteDoc(doc(db, 'bookings', bookingId));
      setBookings(prev => prev.filter(b => b.id !== bookingId));
      setConfirmDeleteId(null);
      console.log("Delete success for ID:", bookingId);
    } catch (err: any) {
      console.error("Delete failed for ID:", bookingId, err);
      handleFirestoreError(err, OperationType.DELETE, 'bookings/' + bookingId);
    }
  };

  const handleClearPast = async () => {
    setIsClearing(true);
    setShowConfirmClear(false);
    console.log("Clearing past bookings...");
    let deletedCount = 0;
    let failedCount = 0;
    try {
      const now = new Date().getTime();
      const pastBookings = bookings.filter(b => new Date(b.endTime).getTime() < now);
      console.log(`Found ${pastBookings.length} past bookings to clear`);
      
      const newBookings = [...bookings];
      
      for (const b of pastBookings) {
        try {
          console.log(`Deleting past booking: ${b.id}`);
          await deleteDoc(doc(db, 'bookings', b.id));
          deletedCount++;
          // Remove from list
          const index = newBookings.findIndex(x => x.id === b.id);
          if (index > -1) newBookings.splice(index, 1);
        } catch (err: any) {
           console.error("Failed to delete booking", b.id, err);
           failedCount++;
        }
      }
      
      setBookings(newBookings);
      console.log(`Clear past completed. Deleted: ${deletedCount}, Failed: ${failedCount}`);
    } catch (err) {
      console.error("Error clearing past bookings:", err);
    } finally {
      setIsClearing(false);
    }
  };

  const bookingUrl = `https://platapost.com/book/${hostId}`;

  const copyUrl = () => {
    navigator.clipboard.writeText(bookingUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const pastBookingsCount = bookings.filter(b => new Date(b.endTime).getTime() < new Date().getTime()).length;

  return (
    <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="p-8 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
            <Calendar className="w-6 h-6 text-indigo-600" />
            Calendar Bookings
          </h2>
          <p className="text-sm text-gray-500">Manage your scheduled demos and chats.</p>
        </div>
        {pastBookingsCount > 0 && (
          <div className="flex items-center gap-2">
            {showConfirmClear ? (
              <div className="flex items-center gap-2 bg-red-50 p-1 rounded-xl">
                 <span className="text-xs font-bold text-red-600 px-2">Clear all?</span>
                 <button 
                  onClick={handleClearPast}
                  disabled={isClearing}
                  className="px-3 py-1.5 bg-red-600 text-white hover:bg-red-700 rounded-lg text-xs font-bold transition"
                >
                  Confirm
                </button>
                <button 
                  onClick={() => setShowConfirmClear(false)}
                  className="px-3 py-1.5 bg-white text-gray-600 hover:bg-gray-100 rounded-lg text-xs font-bold border border-gray-200 transition"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button 
                onClick={() => setShowConfirmClear(true)}
                disabled={isClearing}
                className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 hover:bg-red-100 rounded-xl transition font-medium text-sm disabled:opacity-50"
              >
                {isClearing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Clear Past Bookings ({pastBookingsCount})
              </button>
            )}
          </div>
        )}
      </div>

      <div className="p-8">
        <div className="mb-8 p-6 bg-indigo-50 rounded-2xl border border-indigo-100 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>
            <h3 className="font-bold text-indigo-900 mb-1">Your Booking Link</h3>
            <p className="text-sm text-indigo-700">Share this link with agencies to let them book a 15-minute slot.</p>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <input 
              type="text" 
              readOnly 
              value={bookingUrl} 
              className="bg-white px-4 py-2 rounded-xl text-sm border border-indigo-200 text-gray-600 flex-1 sm:w-64 outline-none"
            />
            <button 
              onClick={copyUrl}
              className="p-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition"
              title="Copy Link"
            >
              {copied ? <CheckCircle2 className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
            </button>
            <a 
              href={bookingUrl}
              target="_blank"
              rel="noreferrer"
              className="p-2 bg-white text-indigo-600 border border-indigo-200 rounded-xl hover:bg-indigo-50 transition"
              title="Open Link"
            >
              <ExternalLink className="w-5 h-5" />
            </a>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-400">Loading bookings...</div>
        ) : bookings.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-2xl border border-dashed border-gray-200">
            <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No bookings yet. Share your link to get started!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {bookings.map(b => {
              const start = new Date(b.startTime);
              const end = new Date(b.endTime);
              const isPast = end.getTime() < new Date().getTime();
              
              return (
                <div key={b.id} className={`p-4 rounded-2xl border flex flex-col md:flex-row items-center justify-between gap-4 ${isPast ? 'bg-gray-50 border-gray-100 opacity-70' : 'bg-white border-gray-200'}`}>
                  <div className="flex items-start gap-4 w-full md:w-auto">
                    <div className={`p-3 rounded-xl flex flex-col items-center justify-center w-16 h-16 shrink-0 ${isPast ? 'bg-gray-100 text-gray-500' : 'bg-indigo-50 text-indigo-600'}`}>
                      <span className="text-xs uppercase font-bold">{format(start, 'MMM')}</span>
                      <span className="text-xl font-black">{format(start, 'd')}</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900">{b.guestName}</h4>
                      <p className="text-sm text-gray-500 mb-1">{b.guestEmail}</p>
                      <p className="text-xs font-bold text-indigo-600 bg-indigo-50 px-2 py-1 rounded-md inline-block">
                        {format(start, 'h:mm a')} - {format(end, 'h:mm a')}
                      </p>
                    </div>
                  </div>
                  
                  <div className="w-full md:w-auto text-right">
                    {confirmDeleteId === b.id ? (
                      <div className="flex items-center gap-2">
                        <button 
                          onClick={() => handleDelete(b.id)}
                          className="px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition text-xs font-bold"
                        >
                          Confirm
                        </button>
                        <button 
                          onClick={() => setConfirmDeleteId(null)}
                          className="px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition text-xs font-bold"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button 
                        onClick={() => setConfirmDeleteId(b.id)}
                        className="text-red-500 hover:bg-red-50 p-2 rounded-lg transition"
                        title="Cancel Booking"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
